import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useViewConfig } from "../hooks/useViewConfig";
import { getSectionBySlug } from "../utils/sectionUtils";
import { getCategoriesBySection } from "../services/categoryService";
import { getProducts } from "../services/productService";
import SectionBanner from "../components/sections/SectionBanner";
import SectionCategories from "../components/sections/SectionCategories";
import SectionProducts from "../components/sections/SectionProducts";
import SectionSkeleton from "../components/sections/SectionSkeleton";
import "../styles/sectionPage.css";

/**
 * Controller/Container component representing the Section Detail Page.
 * Coordinates section configs, categories list, and in-memory filtered products list.
 */
const SectionPage = () => {
  const { slug } = useParams();
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [categoriesError, setCategoriesError] = useState(null);

  // Phase 2 Product States
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [productsError, setProductsError] = useState(null);

  // Fetch HOME_SECTIONS ViewConfig using the existing custom hook
  const { data: configData, loading: loadingSections, error: sectionsError } = useViewConfig("HOME_SECTIONS");

  // Reset selected category chip when slug changes
  useEffect(() => {
    setSelectedCategory("All");
  }, [slug]);

  // Find the current section object based on slug
  const sections = configData?.data?.sections || [];
  const section = getSectionBySlug(sections, slug);

  // Fetch section categories from the backend
  useEffect(() => {
    if (!section) return;

    let isMounted = true;
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        setCategoriesError(null);
        const data = await getCategoriesBySection(section.title);
        if (isMounted) {
          setCategories(data);
        }
      } catch (err) {
        if (isMounted) {
          setCategoriesError(err);
          console.error(`Error fetching categories for section "${section.title}":`, err);
        }
      } finally {
        if (isMounted) {
          setLoadingCategories(false);
        }
      }
    };

    fetchCategories();

    return () => {
      isMounted = false;
    };
  }, [section]);

  // Fetch section products on mount/section changes (Available Only)
  useEffect(() => {
    if (!section) return;

    let isMounted = true;
    const fetchProductsData = async () => {
      try {
        setLoadingProducts(true);
        setProductsError(null);
        // Query backend for only available products belonging to active section
        const data = await getProducts({ section: section.title, isAvailable: true });
        if (isMounted) {
          setProducts(data);
        }
      } catch (err) {
        if (isMounted) {
          setProductsError(err);
          console.error(`Error fetching products for section "${section.title}":`, err);
        }
      } finally {
        if (isMounted) {
          setLoadingProducts(false);
        }
      }
    };

    fetchProductsData();

    return () => {
      isMounted = false;
    };
  }, [section]);

  // Derive category product counts locally using category ID keys (Phase 2 Requirement)
  const categoryCounts = useMemo(() => {
    const counts = { All: products.length };
    categories.forEach((cat) => {
      counts[cat._id] = products.filter(
        (p) => p.category && (p.category._id === cat._id || p.category === cat._id)
      ).length;
    });
    return counts;
  }, [products, categories]);

  // Filter products locally for instant response (Phase 2 Requirement)
  const filteredProducts = useMemo(() => {
    if (selectedCategory === "All") {
      return products;
    }
    return products.filter(
      (p) =>
        p.category &&
        (p.category.name === selectedCategory ||
         (typeof p.category.name === "string" &&
          p.category.name.toLowerCase() === selectedCategory.toLowerCase()))
    );
  }, [products, selectedCategory]);

  // Loading State: Render skeletons for Sections View Config or Category List
  if (loadingSections || (section && loadingCategories)) {
    return <SectionSkeleton />;
  }

  // Error State: ViewConfig fetch error
  if (sectionsError) {
    return (
      <div className="section-page-container" style={{ textAlign: "center", padding: "100px 20px" }}>
        <h2>Error Loading Sections</h2>
        <p style={{ color: "var(--color-text-secondary)", marginTop: "8px" }}>{sectionsError.message || "Something went wrong."}</p>
        <Link to="/" className="btn btn-primary" style={{ marginTop: "24px" }}>
          Back to Home
        </Link>
      </div>
    );
  }

  // Error State: Matched section not found
  if (!section) {
    return (
      <div className="section-page-container" style={{ textAlign: "center", padding: "100px 20px" }}>
        <h2>Section Not Found</h2>
        <p style={{ color: "var(--color-text-secondary)", marginTop: "8px" }}>We couldn't find the section you were looking for.</p>
        <Link to="/" className="btn btn-primary" style={{ marginTop: "24px" }}>
          Back to Home
        </Link>
      </div>
    );
  }

  return (
    <div className="section-page-container">
      {/* 1. Dynamic Section Banner (Breadcrumb included) */}
      <SectionBanner section={section} />

      {/* 2. Dynamic Categories Section (Selection state lifted to parent) */}
      {!categoriesError && (
        <SectionCategories
          categories={categories}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          sectionColor={section.color}
          counts={categoryCounts}
        />
      )}

      {categoriesError && (
        <div style={{ padding: "20px 0", color: "#ef4444" }}>
          <p>Failed to load categories for this section. Please try again later.</p>
        </div>
      )}

      {/* 3. Products Area (Toolbar, Skeletons, empty cards, or list cards) */}
      {!productsError ? (
        <SectionProducts
          products={filteredProducts}
          loading={loadingProducts}
          sectionColor={section.color}
        />
      ) : (
        <div style={{ padding: "20px 0", color: "#ef4444" }}>
          <p>Failed to load products for this section. Please try again later.</p>
        </div>
      )}
    </div>
  );
};

export default SectionPage;
